import React from 'react';
import T from 'i18n-react';
import isEmpty from 'lodash/isEmpty';
import { NotificationLevels, NotificationTypes, Text } from '@adsk/bim360-matrix-react-components';
import naturalCompare from 'alphanum-sort/lib/compare'; // This is the natural sort being used in DOCS
import {
  get as lodashGet,
  keyBy as lodashKeyBy,
  uniq as lodashUniq,
  flatten as lodashFlatten,
} from 'lodash';
import { downloadFile, convertToCsvContent } from '../../utils/downloadFile';
import { getToken } from '../../utils/getToken';
import { toIsoDateWithTime } from '../../utils/date-helpers';
import { getBlob } from '../../utils/blob-helpers';
import { encodeSafeForUrl } from '../../utils/base64-helpers';
import { isImageAttachment, getMimeType } from '../../utils/attachmentsHelper';
import { buildOxygenObjects } from '../../utils/oxygenHelpers';
import {
  isPushpinVisible,
  getPushpinVisibilityTextPrefix,
  isIssueInSheet,
} from '../../utils/issues-helpers';
import { getUserNameDisplayNameWithCompany } from '../../utils/userHelpers';
import { POPUP_VIEWER_URL, POPUP_VIEWER_TITLE } from '../../constants/common';
import { SHOW_TOAST_STATUS_LIST } from '../../constants/transitions';
import { ENV_CONFIGS } from '../../constants/envs';
import { viewTypes } from '../../constants/view-types';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import {
  getPromiseForAsyncNotification,
  trimFileName,
  getNotificationTextForTransition,
} from '../../utils/notificationHelpers';
import TryAgainErrorNotification from '../../components/TryAgainErrorNotification';
import {
  issuesCollectionReset,
  FETCH_RFIS_FOR_CSV_REPORT,
  FETCH_RFIS_FOR_CSV_REPORT_FULFILLED,
  rfisCsvReportDownloaded,
  fetchRfiAsyncAttributes,
  FETCH_RFIS_FOR_CSV_REPORT_REJECTED,
  DELETE_PUSHPIN,
  deletePushpinSuccess,
  deletePushpinFail,
  ADD_LINKED_ENTITIES,
  ADD_LINKED_ENTITIES_API,
  REMOVE_LINKED_ENTITIES,
  ADD_LINKED_ENTITIES_API_FULFILLED,
  REMOVE_LINKED_ENTITIES_API,
  ADD_LINKED_ENTITIES_API_REJECTED,
  REMOVE_LINKED_ENTITIES_API_FULFILLED,
  REMOVE_LINKED_ENTITIES_API_REJECTED,
  VIRTUAL_FOLDER_CREATE_START,
  VIRTUAL_FOLDER_CREATE_API,
  VIRTUAL_FOLDER_CREATE_API_FULFILLED,
  VIRTUAL_FOLDER_CREATE_API_REJECTED,
} from '../actions/issues-actions';
import {
  getFetchParams,
  getQueryKey,
  getLastQueryKey,
  getQueryKeyIsCurrent,
  getRFIs,
  getPendingIssue,
  getPendingIssueForm,
  getActivePushpinAttributes,
  getCurrentSheets,
  getProjectName,
  getProjectId,
  getApigeeHost,
  getEnv,
  getRegion,
  getDocumentCollection,
  getFetchingIssuesUrns,
  getCurrentViewType,
  getDocumentVersion,
  getCurrentUser,
  getOxygenMappings,
  getDerivativeServiceHost,
  getCurrentPreviewIssueAttributes,
  getAttachmentsList,
  getCurrentProjectScope,
  getCurrentPreviewIssueId,
  currentUserRoleForAnalytics,
} from '../selectors';
import { attachmentOrigin, DM_URN_TYPE, OSS_URN_TYPE } from '../../constants/attachment-types';
import {
  JSONAPI_CONFIGURE,
  JSONAPI_CONFIGURE_DOCS_SDK,
  OXYGEN_CONFIGURE_START,
  OXYGEN_CONFIGURE_ASYNC,
  OXYGEN_CONFIGURE_ASYNC_FULFILLED,
  CURRENT_USER_FETCH_START,
  CURRENT_USER_FETCH_API,
  CURRENT_DOCS_PROJECT_FETCH_START,
  ISSUES_FETCH_START,
  ISSUES_FETCH_END,
  ISSUES_FETCH_LOADING_START,
  ISSUES_FETCH_API,
  ISSUES_FETCH_API_FULFILLED,
  ISSUES_FETCH_PAYLOAD_INJECT,
  ISSUE_INSTANCE_FETCH_START,
  ISSUE_INSTANCE_FETCH_API,
  ISSUE_INSTANCE_FETCH_API_FULFILLED,
  ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT,
  ISSUES_FETCH_REPORT_START,
  ISSUES_FETCH_REPORT_API,
  ISSUES_FETCH_REPORT_API_FULFILLED,
  ISSUES_FETCH_REPORT_API_REJECTED,
  RFI_CREATE_START,
  RFI_CREATE_API,
  RFI_CREATE_API_FULFILLED,
  RFI_CREATE_API_REJECTED,
  RFI_UPDATE_START,
  RFI_UPDATE_API,
  RFI_UPDATE_API_FULFILLED,
  RFI_UPDATE_API_REJECTED,
  CHANNEL_ITEMS_FETCH_START,
  CHANNEL_ITEMS_FETCH_API,
  ATTACHMENTS_FETCH_START,
  ATTACHMENTS_FETCH_API,
  COMMENT_CREATE_START,
  COMMENT_CREATE_API,
  COMMENT_CREATE_API_FULFILLED,
  COMMENT_CREATE_API_REJECTED,
  ATTACHMENT_UPLOAD_FINISHED,
  ATTACHMENT_UPLOAD_FAILED,
  ATTACHMENT_CREATE_API,
  ATTACHMENT_CREATE_API_FULFILLED,
  FETCH_PROJECT_FOLDERS,
  FETCH_FOLDER_CONTENTS,
  CREATE_ATTACHMENT_FROM_DOCUMENT,
  CHANNEL_ITEMS_FETCH_API_FULFILLED,
  SAVE_ISSUE,
  FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS,
  DOCUMENT_THUMBNAIL_URL_START,
  DOCUMENT_THUMBNAIL_URL_FULFILLED,
  ATTACHMENTS_FETCH_API_FULFILLED,
  DOCUMENTS_WITH_ISSUES_FETCH,
  DOCUMENT_COLLECTION_CONFIGURE,
  HANDLE_OPEN_ATTACHMENT,
  OPEN_ATTACHMENT,
  HANDLE_NAVIGATE_TO_RFI_IN_DOCUMENT,
  OPEN_PUSHPIN_SELECTION,
  CLOSE_PUSHPIN_SELECTION,
  ISSUES_FETCH_SEARCH_START,
  ISSUES_FETCH_SEARCH_API,
  TOGGLE_PUSHPIN_VISIBILITY,
  TOGGLE_PUSHPIN_VISIBILITY_SUCCESS,
  TOGGLE_PUSHPIN_VISIBILITY_FAIL,
  ATTACHMENT_DELETE_API,
  ATTACHMENT_DELETE_API_REJECTED,
  ATTACHMENT_DELETE_API_FULFILLED,
  DELETE_ATTACHMENT,
  CREATE_ATTACHMENT_FROM_DOCUMENT_START,
  VALIDATE_RFI_CUSTOM_IDENTIFIER_START,
  VALIDATE_RFI_CUSTOM_IDENTIFIER_API,
  VALIDATE_RFI_CUSTOM_IDENTIFIER_API_FULFILLED,
  FETCH_RFI_CUSTOM_IDENTIFIER_START,
  FETCH_RFI_CUSTOM_IDENTIFIER_API,
  FETCH_RFI_CUSTOM_IDENTIFIER_API_FULFILLED,
  batchClearPushpinAction,
  batchCreatePushpinAction,
  updateIssueAction,
  updatePushpinAction,
  sheetChangeAction,
  currentProjectFetchFinished,
  currentProjectFetchFailed,
  showNotificationAction,
  clearUploadingAttachmentsListByOrigin,
  changeLocationAction,
  trackEvent,
  openIframeModal,
  fetchUrns,
} from '../actions';
import { ISSUE_FILTER_PROPERTIES } from '../../constants/issue-query-params';
import {
  togglePushpinVisibility,
  getAssigneeTypeByAssigneeId,
  deleteAttachment,
  statusChange,
  assigneeChange,
  addAttachment,
  getUserRole,
  exportList,
  fieldEdit,
  linkReference,
  unlinkReference,
} from '../../utils/analytics-helpers';
import { STATUS_VOID } from '../../constants/status-types';
import { attachmentTypes, reportFormats } from '../../constants/analytics';
import featureFlags, { FEATURE_FLAGS } from '../../utils/featureFlags';
import rfisCsvMapping from '../../constants/rfiCsvMaping';
import { FOLDER_CONTENT_PAGE_LIMIT } from '../../constants/docsApi';
import { getCreateCommentErrorMessage } from '../../utils/jsonapi-helpers';
import { getStatusFromServerStatus } from '../../utils/transition-helpers';
import { getS3DownloadUrl } from '../../utils/getS3UrlFromOss';

const commitTransitionsFlow = (store, issue, transitionsFlow, transitionAttributes, meta) => {
  if (transitionsFlow.length) {
    const transition = issue.permittedTransitions.find(
      (permittedTransition) => permittedTransition.id === transitionsFlow[0]
    );
    if (transition) {
      store.dispatch(
        updateIssueAction(
          issue,
          {
            transitionId: transition.id,
            ...transitionAttributes,
          },
          { ...meta, transitionsFlow: transitionsFlow.slice(1), transitionAttributes: {} }
        )
      );
      // false means the statuses transitions flow is not yet completed
      return false;
    }
  }
  // true means the statuses transitions flow is completed
  return true;
};

let docsSDK;

/**
 * jsonApi instance:
 */

// TODO: Move all *_START actions to thunk actions, that returns the promise itself. Then use the promise to operate on success/fail from the components.
export default (jsonApi, docsSDKParam, redispatcher) => {
  return (store) => (next) => (action) => {
    // This is required for when rfis-ui is mounted (not loaded as a smart component). When rfis-ui is loaded
    // as a smart component, the DocsSDK is initialized in the RFIsProvider and set via JSONAPI_CONFIGURE_DOCS_SDK
    if (!docsSDK) {
      docsSDK = docsSDKParam;
    }
    const state = store.getState();

    // eslint-disable-next-line default-case
    switch (action.type) {
      case JSONAPI_CONFIGURE:
        jsonApi.setConfig(action.payload);
        break;
      case JSONAPI_CONFIGURE_DOCS_SDK:
        docsSDK = action.payload;
        break;
      case OXYGEN_CONFIGURE_START: {
        const { usersPromise, companiesPromise, rolesPromise } = action.payload;
        store.dispatch({
          type: OXYGEN_CONFIGURE_ASYNC,
          payload: Promise.all([usersPromise, companiesPromise, rolesPromise]).then(
            ([users, companies, roles]) => {
              return { users, companies, roles };
            }
          ),
        });
        break;
      }
      case OXYGEN_CONFIGURE_ASYNC_FULFILLED: {
        jsonApi.configureOxygenMappings(buildOxygenObjects(action.payload));
        break;
      }
      case CURRENT_USER_FETCH_START:
        store.dispatch({
          type: CURRENT_USER_FETCH_API,
          payload: jsonApi.getCurrentUser(),
        });
        break;
      case CURRENT_DOCS_PROJECT_FETCH_START:
        docsSDK
          .getProjectById(getProjectId(state))
          .then((project) => store.dispatch(currentProjectFetchFinished(project)))
          .catch((error) => store.dispatch(currentProjectFetchFailed(error)));
        break;
      case ISSUES_FETCH_REPORT_START: {
        const today = toIsoDateWithTime(Date.now());
        const textRFIsLabel = T.translate('rfis.label_capitalized');
        const query = getFetchParams(state);
        const sortBy = '-custom_identifier';
        const queryParams = { ...query.queryParams, csvVersion: 1, sort: sortBy, format: 'csv' };
        const csvQuery = { queryParams };

        if (query.targetUrn) {
          csvQuery.targetUrn = query.targetUrn;
          csvQuery.documentVersion = query.documentVersion;
        }

        const csvExportAsyncNotificationPromise = getPromiseForAsyncNotification();
        const fileName = `${getProjectName(state)} - ${textRFIsLabel} - ${today}.csv`;

        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: NotificationLevels.SUCCESS,
            message: (
              <Text hasHTML>
                {T.translate('notifications.preparingToDownload', {
                  fileName: trimFileName(fileName, 35),
                })}
              </Text>
            ),
            autoDismiss: 0.5,
            asyncOperation: csvExportAsyncNotificationPromise.promise,
            closeButtonTooltipText: T.translate('notifications.downloadWillContinue'),
          })
        );

        csvQuery.limit = 'max';
        store.dispatch({
          type: FETCH_RFIS_FOR_CSV_REPORT,
          meta: { fileName, csvExportAsyncNotificationPromise, csvQuery },
          payload: jsonApi.getRfis(csvQuery),
        });
        const viewType = getCurrentViewType(state);
        store.dispatch(trackEvent(exportList(reportFormats.CSV, viewType)));

        break;
      }

      case FETCH_RFIS_FOR_CSV_REPORT_FULFILLED: {
        if (!action.payload.length) {
          store.dispatch({
            type: ISSUES_FETCH_REPORT_API_REJECTED,
            meta: action.meta,
          });
          break;
        }
        const { prevRfis = [] } = action.meta;
        const {
          page: { offset, limit },
          recordCount,
        } = action.payload[0].meta;
        const fetchMore = recordCount - offset > limit;
        const totalRfis = prevRfis.concat(action.payload);
        if (fetchMore) {
          const newOffset = offset + limit;
          const newMeta = {
            ...action.meta,
            prevRfis: totalRfis,
          };
          newMeta.csvQuery = { ...newMeta.csvQuery, offset: newOffset };
          store.dispatch({
            type: FETCH_RFIS_FOR_CSV_REPORT,
            meta: newMeta,
            payload: jsonApi.getRfis(newMeta.csvQuery),
          });
        } else {
          action.meta.csvExportAsyncNotificationPromise.resolve();
          const csvContent = convertToCsvContent(rfisCsvMapping, totalRfis, true);
          const { fileName } = action.meta;
          downloadFile(csvContent, fileName, 'text/csv;encoding:utf-8');
          store.dispatch(rfisCsvReportDownloaded());
        }
        break;
      }

      case ISSUES_FETCH_REPORT_API_FULFILLED: {
        action.meta.csvExportAsyncNotificationPromise.resolve();
        downloadFile(action.payload[0].payload, action.meta.fileName, 'text/csv;encoding:utf-8');
        break;
      }
      case FETCH_RFIS_FOR_CSV_REPORT_REJECTED:
      case ISSUES_FETCH_REPORT_API_REJECTED: {
        const { csvExportAsyncNotificationPromise, fileName } = action.meta;

        csvExportAsyncNotificationPromise.reject();
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: (
              <TryAgainErrorNotification
                message={T.translate('notifications.failedToDownload', {
                  fileName: trimFileName(fileName, 35),
                })}
                tryAgainAction={() => store.dispatch({ type: ISSUES_FETCH_REPORT_START })}
              />
            ),
          })
        );
        break;
      }
      case ISSUES_FETCH_SEARCH_START: {
        const search = action.payload;
        let query = getFetchParams(state);
        query = {
          ...query,
          queryParams: { ...query.queryParams, [ISSUE_FILTER_PROPERTIES.SEARCH]: search },
          limit: 5,
        };
        store.dispatch({
          type: ISSUES_FETCH_SEARCH_API,
          meta: { query },
          payload: jsonApi.getRfis(query),
        });
        break;
      }
      case ISSUES_FETCH_START: {
        const query = getFetchParams(state);
        const queryKey = getQueryKey(state);
        const lastQueryKey = getLastQueryKey(state);
        // TODO - after we merge issue visibility feature, we can just check if we're in project view instead of having the component specify "resetCollection"
        const { resetCollection } = action.payload;

        if (lastQueryKey !== queryKey || resetCollection) {
          store.dispatch({ type: ISSUES_FETCH_LOADING_START });
          // Clear collection (and pushpins) when offset in query params is 0.
          // If resetCollection was specified, we clear the collection after we get the data.
          if (query.offset === 0 && !resetCollection) {
            const currentRFIs = getRFIs(state);
            store.dispatch(issuesCollectionReset());
            store.dispatch(batchClearPushpinAction(currentRFIs));
          }
          store.dispatch({
            type: ISSUES_FETCH_API,
            meta: { query, queryKey, resetCollection },
            payload: jsonApi.getRfis(query),
          });
        }
        break;
      }
      case ISSUES_FETCH_API_FULFILLED: {
        // "meta" from FSA:
        const {
          type,
          query,
          query: { offset, limit },
          queryKey,
          resetCollection,
        } = action.meta;
        // if the following does not obtain, batch requests are interrupted,
        // and current payload is not injected:
        if (getQueryKeyIsCurrent(state, type, queryKey)) {
          // Reset collection before populating it with new data when a specific offset was requested
          if (resetCollection) {
            const currentlyViewedId =
              getCurrentViewType(state) === viewTypes.PROJECT
                ? getCurrentPreviewIssueId(state)
                : null;
            store.dispatch(issuesCollectionReset({ currentlyViewedId }));
          }

          let batchFetchComplete = true;
          // redispatch action and payload for injection:
          store.dispatch({ ...action, type: ISSUES_FETCH_PAYLOAD_INJECT });

          // Map issues targetUrn and use DocsSDK to fetch documents by urns
          const documentCollection = getDocumentCollection(state);
          const IssuesUrns = lodashUniq(action.payload.map((issue) => issue.targetUrn)).filter(
            (urn) => urn && !documentCollection[urn]
          );

          store.dispatch(fetchUrns(IssuesUrns));

          // Batch-create pushpins using entire payload:
          store.dispatch(batchCreatePushpinAction(action.payload));

          if (action.payload.length === limit && !resetCollection) {
            // continue with chained, batch requests...
            const firstRecord = action.payload[0];
            // "meta" from IssuesJsonApi: (for determining length of entire issues collection)
            const {
              meta: { recordCount },
            } = firstRecord;
            const newOffset = offset + limit;
            if (newOffset < recordCount) {
              batchFetchComplete = false;
              const newQuery = { ...query, ...{ offset: newOffset } };
              store.dispatch({
                type: ISSUES_FETCH_API,
                meta: { type, query: newQuery, queryKey },
                payload: jsonApi.getRfis(newQuery),
              });
            }
          }
          if (batchFetchComplete) {
            store.dispatch({ type: ISSUES_FETCH_END });
          }
        }
        break;
      }
      case ISSUE_INSTANCE_FETCH_START:
        store.dispatch({
          type: ISSUE_INSTANCE_FETCH_API,
          meta: { ...action.meta, id: action.payload },
          payload: jsonApi.getRfiById(action.payload),
        });
        break;
      case ISSUE_INSTANCE_FETCH_API_FULFILLED: {
        // In case the issue has sheet details and we also have the currently viewed sheet details,
        // check for a match in either sheet guid or sheet name. If both are different, we trigger
        // the SHEET_CHANGE event for the using app to catch and change the currently viewed sheet accordingly.
        const currentSheets = getCurrentSheets(state);
        if (currentSheets.length && !isIssueInSheet(action.payload, currentSheets)) {
          store.dispatch(
            sheetChangeAction({
              guid: action.payload.sheetMetadata.sheetGuid,
              name: action.payload.sheetMetadata.sheetName,
            })
          );
        }

        const documentCollection = getDocumentCollection(state);
        if (action.payload.targetUrn && !documentCollection[action.payload.targetUrn]) {
          store.dispatch(fetchUrns(action.payload.targetUrn));
        }

        // redispatch action and payload for injection:
        store.dispatch({ ...action, type: ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT });

        break;
      }
      case RFI_CREATE_START: {
        store.dispatch({
          type: RFI_CREATE_API,
          meta: action.meta,
          payload: jsonApi.createRfi(action.payload),
        });

        const issueTargetUrn = action.payload.targetUrn;
        if (getCurrentViewType(state) === viewTypes.PROJECT && issueTargetUrn) {
          const documentCollection = getDocumentCollection(state);
          const fetchingIssuesUrns = getFetchingIssuesUrns(state);
          const documentExistsOrLoading =
            fetchingIssuesUrns.includes(issueTargetUrn) || documentCollection[issueTargetUrn];
          if (!documentExistsOrLoading) {
            store.dispatch(fetchUrns(issueTargetUrn));
          }
        }
        break;
      }
      case RFI_CREATE_API_FULFILLED: {
        const { transitionAttributes, onSuccess } = action.meta;
        const issue = action.payload;
        const viewType = getCurrentViewType(state);
        // fetch async virtualFolderUrn attribute
        store.dispatch(fetchRfiAsyncAttributes(issue.id));
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: NotificationLevels.SUCCESS,
            message: getNotificationTextForTransition(
              viewType,
              'new.alert.created',
              issue.id,
              transitionAttributes.customIdentifier
            ),
          })
        );
        store.dispatch(batchCreatePushpinAction([issue]));
        onSuccess && onSuccess(issue);
        break;
      }
      case RFI_CREATE_API_REJECTED: {
        store.dispatch(clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM));
        const { onError } = action.meta;
        const error = action.payload && action.payload.response;
        onError && onError(error);

        const errors = lodashGet(error, 'data.errors');
        if (errors) {
          const customIdentifierError = errors.find((err) => {
            const pointer = lodashGet(err, 'source.pointer');
            return pointer && pointer.includes('custom_identifier');
          });
          if (customIdentifierError) {
            store.dispatch(
              showNotificationAction({
                type: NotificationTypes.SNACKBAR,
                level: NotificationLevels.ERROR,
                message: T.translate('edit.error.rfiCustomIdentifierIsInvalid'),
              })
            );
          }
        }
        break;
      }
      case RFI_UPDATE_START: {
        const { issue, attributes } = action.payload;
        // Required for analytics events, we calc them here so we won't have to
        // carry the entire `attributes` object to RFI_UPDATE_API action
        const isChangingAssignee =
          attributes[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] &&
          attributes[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] !==
            issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO];
        const isAddingComment = !!attributes[ISSUE_ATTRIBUTE_NAMES.COMMENTS_ATTRIBUTES];
        const hasNewLinkedDocument = !!attributes.targetUrn;

        store.dispatch({
          type: RFI_UPDATE_API,
          meta: {
            changeToStatus: lodashGet(action.meta, 'changeToStatus'),
            rfiCustomIdentifierAnalyticsData: lodashGet(
              action.meta,
              'rfiCustomIdentifierAnalyticsData'
            ),
            id: issue.id,
            customIdentifier: issue.customIdentifier,
            onSuccess: action.meta.onSuccess,
            onError: action.meta.onError,
            transitionsFlow: action.meta.transitionsFlow,
            isChangingAssignee,
            isAddingComment,
            attributes,
            showCreateRevisionField: action.meta.showCreateRevisionField,
          },
          payload: jsonApi.updateRfi(issue, attributes),
        });
        if (hasNewLinkedDocument) {
          store.dispatch(fetchUrns(attributes.targetUrn));
        }
        break;
      }
      case RFI_UPDATE_API_FULFILLED: {
        const {
          transitionsFlow = [],
          transitionAttributes = {},
          onSuccess,
          changeToStatus,
          rfiCustomIdentifierAnalyticsData,
          showCreateRevisionField,
        } = action.meta;
        const issue = action.payload;
        const currentIssue = getCurrentPreviewIssueAttributes(state);
        const normalizedStatus = getStatusFromServerStatus(changeToStatus || '');
        if (!!changeToStatus && SHOW_TOAST_STATUS_LIST.includes(normalizedStatus)) {
          const viewType = getCurrentViewType(state);
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.SUCCESS,
              message: getNotificationTextForTransition(
                viewType,
                `edit.transitionToast.${normalizedStatus}.success`,
                issue.id,
                issue.customIdentifier
              ),
            })
          );
        }

        const transitionsFlowCompleted = commitTransitionsFlow(
          store,
          issue,
          transitionsFlow,
          transitionAttributes,
          action.meta
        );
        if (transitionsFlowCompleted) {
          const { isChangingAssignee, isAddingComment } = action.meta;

          if (changeToStatus) {
            // Track statusChange analytics event
            const workflowType = lodashGet(getCurrentUser(state), 'attributes.rfis.workflowType');
            store.dispatch(
              trackEvent(
                statusChange(
                  currentIssue.status,
                  changeToStatus,
                  isChangingAssignee,
                  isAddingComment,
                  rfiCustomIdentifierAnalyticsData,
                  workflowType,
                  showCreateRevisionField
                )
              )
            );
          } else if (isChangingAssignee) {
            // Track assigneeChange analytics event
            const currentUserId = lodashGet(getCurrentUser(state), 'attributes.id');
            const prevAssignee = getAssigneeTypeByAssigneeId(
              currentIssue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
              currentUserId,
              getOxygenMappings(state),
              currentIssue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
            );
            const newAssignee = getAssigneeTypeByAssigneeId(
              issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
              currentUserId,
              getOxygenMappings(state),
              issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
            );
            store.dispatch(trackEvent(assigneeChange(prevAssignee, newAssignee)));
          }

          // no need to update changesets if the item got voided
          if (changeToStatus !== STATUS_VOID) {
            // dispatch channel-items fetch, for new changeset:
            store.dispatch({
              type: CHANNEL_ITEMS_FETCH_API,
              meta: {
                id: action.meta.id,
                changeToStatus,
                issue: getCurrentPreviewIssueAttributes(state),
              },
              payload: jsonApi.getChannelItems(action.meta.id),
            });
          }

          // Update issue pushpin
          if (issue.pushpinAttributes) {
            const {
              id,
              status,
              pushpinAttributes: {
                location: position,
                objectId,
                viewerState,
                externalId,
                attributesVersion,
              },
            } = issue;
            const label = issue.customIdentifier || T.translate('unspecifiedValue');
            store.dispatch(
              updatePushpinAction({
                id,
                label,
                status,
                position,
                objectId,
                viewerState,
                externalId,
                attributesVersion,
              })
            );
          }

          // Success callback
          onSuccess && onSuccess(issue);
        }
        break;
      }
      case RFI_UPDATE_API_REJECTED: {
        const { onError, changeToStatus, customIdentifier, id } = action.meta;
        onError && onError();

        const errors = lodashGet(action, 'payload.response.data.errors');
        let customIdentifierError = false;
        if (errors) {
          customIdentifierError = errors.find((error) => {
            const pointer = lodashGet(error, 'source.pointer');
            return pointer && pointer.includes('custom_identifier');
          });
        }

        if (customIdentifierError) {
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: T.translate('edit.error.rfiCustomIdentifierIsInvalid'),
            })
          );
        } else if (!!changeToStatus && SHOW_TOAST_STATUS_LIST.includes(changeToStatus)) {
          const viewType = getCurrentViewType(state);

          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: getNotificationTextForTransition(
                viewType,
                `edit.transitionToast.${changeToStatus}.fail`,
                id,
                customIdentifier
              ),
            })
          );
        } else {
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: T.translate('edit.error.general'),
            })
          );
        }

        break;
      }
      case CHANNEL_ITEMS_FETCH_START:
        store.dispatch({
          type: CHANNEL_ITEMS_FETCH_API,
          meta: { id: action.payload, issue: getCurrentPreviewIssueAttributes(state) },
          payload: jsonApi.getChannelItems(action.payload),
        });
        break;
      case ATTACHMENTS_FETCH_START:
        store.dispatch({
          type: ATTACHMENTS_FETCH_API,
          meta: { id: action.payload },
          payload: jsonApi.getAttachments(action.payload),
        });
        break;
      case COMMENT_CREATE_START: {
        store.dispatch({
          type: COMMENT_CREATE_API,
          meta: { id: action.meta.id, resolve: action.meta.resolve, reject: action.meta.reject },
          payload: jsonApi.createComment(action.meta.id, action.payload),
        });
        break;
      }
      case ATTACHMENT_CREATE_API_FULFILLED:
      case COMMENT_CREATE_API_FULFILLED: {
        if (action.meta.resolve) {
          action.meta.resolve();
        }
        // dispatch channel-items fetch, for new comment or attachment:
        store.dispatch({
          type: CHANNEL_ITEMS_FETCH_API,
          meta: { id: action.meta.id, issue: getCurrentPreviewIssueAttributes(state) },
          payload: jsonApi.getChannelItems(action.meta.id),
        });

        if (action.type === ATTACHMENT_CREATE_API_FULFILLED) {
          // Track addAttachment analytics event
          const { name, urnType } = action.payload;
          const { origin } = action.meta;
          const attachmentType =
            urnType === OSS_URN_TYPE ? attachmentTypes.UPLOAD : attachmentTypes.LINK;

          store.dispatch(trackEvent(addAttachment(attachmentType, name, origin)));
        }

        break;
      }
      case COMMENT_CREATE_API_REJECTED: {
        if (action.meta.reject) {
          action.meta.reject();
        }
        const errorResponse =
          action.payload && action.payload.response ? action.payload.response.data : {};
        const errorMessage = getCreateCommentErrorMessage(errorResponse);

        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: errorMessage,
          })
        );
        break;
      }
      case ATTACHMENT_UPLOAD_FINISHED: {
        const { createAfterUpload, origin } = action.meta;
        if (createAfterUpload) {
          // Create attachment only if attachment is not for create issue.
          const { fileId } = action.meta;
          const issueId = action.meta.id;
          store.dispatch({
            type: ATTACHMENT_CREATE_API,
            meta: { id: issueId, fileId, origin },
            payload: jsonApi.createAttachment(issueId, action.payload),
          });
        }
        break;
      }
      case ATTACHMENT_UPLOAD_FAILED: {
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: T.translate('errors.attachmentUploadError'),
          })
        );
        break;
      }
      case CHANNEL_ITEMS_FETCH_API_FULFILLED: {
        store.dispatch({
          type: DOCUMENT_THUMBNAIL_URL_START,
          payload: action.payload,
          meta: action.meta,
        });
        break;
      }
      case FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS:
      case ATTACHMENTS_FETCH_API_FULFILLED: {
        store.dispatch({
          type: DOCUMENT_THUMBNAIL_URL_START,
          payload: action.payload,
        });
        break;
      }
      case DOCUMENT_THUMBNAIL_URL_START: {
        // Generate blob urls data for all attachments

        // Get blob for uploaded image attachments
        const existingThumbnails = state.thumbnails.list;
        const projectScope = getCurrentProjectScope(state);
        const imageAttachments = action.payload.filter(
          (attachment) =>
            attachment.urnType === OSS_URN_TYPE &&
            isImageAttachment(attachment) &&
            !existingThumbnails[attachment.urn]
        );
        const imageAttachmentsThumbnailPromise = new Promise((resolve) => {
          const blobPromises = imageAttachments
            .map((attachment) => {
              const mimeType = getMimeType(attachment.name); // Files uploaded from the user's computer can only be of type png or jpeg
              return getToken(store)
                .then((token) =>
                  getS3DownloadUrl({ url: `${attachment.url}`, token, projectScope })
                )
                .then(({ url }) => {
                  return getBlob(url, mimeType, null, projectScope);
                })
                .then((blob) => ({ ...attachment, error: false, blob }))
                .catch(() => ({ ...attachment, error: true }));
            })
            .filter((blobPromise) => !!blobPromise);

          Promise.all(blobPromises).then((blobs) => resolve(blobs));
        });

        // Get blob for linked dm attachments
        const documentAttachments = action.payload.filter(
          (attachment) => attachment.urnType === DM_URN_TYPE
        );
        const documentsUrns = documentAttachments.map((attachment) => attachment.urn);

        const changesetsUrns = action.payload
          .filter((changeset) => lodashGet(changeset, 'changes.linkedDocument[1]'))
          .map((changeset) => changeset.changes.linkedDocument[1]);

        const currentIssue = getCurrentPreviewIssueAttributes(state);
        if (lodashGet(currentIssue, 'targetUrn')) {
          documentsUrns.push(currentIssue.targetUrn);
        }

        const uniqDocumentsUrns = lodashUniq([...documentsUrns, ...changesetsUrns]);

        // eslint-disable-next-line consistent-return
        const documentAttachmentsThumbnailPromise = new Promise((resolve) => {
          if (!uniqDocumentsUrns.length) {
            return resolve();
          }
          // Fetch documents data by urns so we can fetch their thumbnail using their bubble_urn
          docsSDK.getDocumentsByURNs(getProjectId(state), uniqDocumentsUrns).then((result) => {
            const blobPromises = result.map((documentAttachmentData) => {
              if (documentAttachmentData.currentVersion.bubbleUrn) {
                let thumbnailUrl = `${getApigeeHost(state)}${getDerivativeServiceHost(
                  state
                )}/thumbnails/${encodeSafeForUrl(
                  documentAttachmentData.currentVersion.bubbleUrn
                )}?type=small`;
                if (documentAttachmentData.currentVersion.bubbleViewableGuid) {
                  thumbnailUrl += `&guid=${documentAttachmentData.currentVersion.bubbleViewableGuid}`;
                }
                return getToken(store)
                  .then((token) => getBlob(thumbnailUrl, 'image/png', token, projectScope))
                  .then((blob) => ({ ...documentAttachmentData, error: false, blob }))
                  .catch(() => ({ ...documentAttachmentData, error: true }));
              }
              // eslint-disable-next-line promise/no-return-wrap
              return Promise.resolve({ ...documentAttachmentData });
            });

            Promise.all(blobPromises).then((blobs) => resolve(blobs));
          });
        });

        Promise.all([imageAttachmentsThumbnailPromise, documentAttachmentsThumbnailPromise]).then(
          (attachmentArrays) => {
            // Each attachment promise returns an array (of blobs), we need to flatten it and filter to keep object only where we actually have a blob
            const flattenedAndFilteredBlobs = lodashFlatten(attachmentArrays).filter(
              (attachment) => attachment && !!attachment.blob
            );
            const attachmentsData = lodashKeyBy(
              flattenedAndFilteredBlobs.map((attachment) => ({
                attachmentUrn: attachment.urn,
                attachmentUrl: URL.createObjectURL(attachment.blob),
                name: attachment.name,
                error: attachment.error,
              })),
              'attachmentUrn'
            );

            store.dispatch({
              type: DOCUMENT_THUMBNAIL_URL_FULFILLED,
              payload: attachmentsData,
            });
          }
        );

        break;
      }
      case DOCUMENTS_WITH_ISSUES_FETCH: {
        if (!isEmpty(action.payload)) {
          docsSDK.getDocumentsByURNs(getProjectId(state), action.payload).then((result) => {
            const documentCollection = {};
            // Init objects for all requested URNs. That way unavailable document URNs exist as keys with empty values,
            // so we can differ between not yet requested documents & not available ones.
            action.payload.forEach((urn) => {
              documentCollection[urn] = {};
            });
            result.forEach((document) => {
              documentCollection[document.urn] = {
                urn: document.urn,
                name: document.currentVersion.title,
                version: document.latestVersion,
                bubbleUrn: document.currentVersion.bubbleUrn,
                fileName: document.currentVersion.fileName,
              };
            });
            store.dispatch({
              type: DOCUMENT_COLLECTION_CONFIGURE,
              payload: documentCollection,
            });
          });
        }

        break;
      }
      case FETCH_PROJECT_FOLDERS: {
        docsSDK
          .getFoldersListByProjectId(getProjectId(state))
          .then((data) => action.payload.resolve(data))
          .catch((error) => action.payload.reject(error));

        break;
      }
      case FETCH_FOLDER_CONTENTS: {
        const sortByObjKey = (key) => (a = '', b = '') => {
            let A;
            let B;

            if (typeof a[key] === 'string') {
              A = a[key].toLowerCase();
            }
            if (typeof b[key] === 'string') {
              B = b[key].toLowerCase();
            }

            return naturalCompare({ sign: false, insensitive: true }, A, B);
          };
        const sortResults = (data) => {
          if (data.folders) {
            data.folders.sort(sortByObjKey('title'));
          }
          if (data.documents) {
            data.documents.sort(sortByObjKey('name'));
          }

          return data;
        };

        const getFolderContentsBatch = () => {
          const folderContent = {
            documents: [],
            folders: [],
          };

          const getFolderContentChunk = (offset = 0) => {
            return docsSDK
              .getFolderContents(getProjectId(state), action.payload.urn, {
                ...action.payload.options,
                offset,
              })
              .then((results) => {
                folderContent.documents = [...folderContent.documents, ...results.documents];
                folderContent.folders = results.folders;
                if (results.hasNextPage) {
                  return getFolderContentChunk(offset + FOLDER_CONTENT_PAGE_LIMIT);
                }
                return folderContent;
              });
          };

          return getFolderContentChunk(0);
        };

        const fetchFolderContent = featureFlags.get(FEATURE_FLAGS.RFIS_FETCH_ALL_DOCUMENTS_BATCHES)
          ? getFolderContentsBatch()
          : docsSDK.getFolderContents(
              getProjectId(state),
              action.payload.urn,
              action.payload.options
            );

        fetchFolderContent
          .then(sortResults)
          .then((data) => action.payload.resolve(data))
          .catch((error) => action.payload.reject(error));
        break;
      }
      case CREATE_ATTACHMENT_FROM_DOCUMENT: {
        const { document, origin } = action.payload;
        const { issueId } = action.payload;
        const { urn } = document;
        const attachments = getAttachmentsList(state);
        const isExistingAttachment =
          attachments.findIndex((attachment) => attachment.urn === urn) !== -1;
        if (isExistingAttachment) {
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: T.translate('errors.attachmentExistsError'),
            })
          );
        } else {
          store.dispatch({
            type: CREATE_ATTACHMENT_FROM_DOCUMENT_START,
            payload: action.payload,
            meta: { id: issueId, fileId: urn, origin },
          });
        }
        break;
      }
      case CREATE_ATTACHMENT_FROM_DOCUMENT_START: {
        const { document } = action.payload;
        const { issueId } = action.payload;
        const { origin } = action.meta;
        const name = document.currentVersion.title;
        const urnType = DM_URN_TYPE;
        const { urn } = document;
        store.dispatch({
          type: ATTACHMENT_CREATE_API,
          meta: { id: issueId, fileId: urn, origin },
          payload: jsonApi.createAttachment(issueId, {
            name,
            urn,
            urnType,
          }),
        });
        break;
      }
      case DELETE_ATTACHMENT: {
        const { issueId, attachmentId, analyticsObj } = action.payload;

        store.dispatch({
          type: ATTACHMENT_DELETE_API,
          meta: { attachmentId, analyticsObj },
          payload: jsonApi.deleteAttachment({ issueId, attachmentId }),
        });
        break;
      }
      case ATTACHMENT_DELETE_API_FULFILLED: {
        // track attachment deletion event
        const { isOwner, status, assignedTo, assignedToType } = action.meta.analyticsObj;

        const oxygen = getOxygenMappings(state);
        const currentUser = getCurrentUser(state);
        const currentUserId = lodashGet(currentUser, 'attributes.id');
        const assignee = getAssigneeTypeByAssigneeId(
          assignedTo,
          currentUserId,
          oxygen,
          assignedToType
        );

        const viewType = getCurrentViewType(state);
        const deletedBy = getUserRole(
          currentUser.attributes.rfis.workflowRoles,
          currentUser.attributes.isProjectAdmin
        );

        store.dispatch(
          trackEvent(deleteAttachment(viewType, assignee, isOwner, status, deletedBy))
        );

        break;
      }
      case ATTACHMENT_DELETE_API_REJECTED: {
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: T.translate('errors.attachmentDeleteError'),
          })
        );
        break;
      }
      case SAVE_ISSUE: {
        const pendingIssueForm = getPendingIssueForm(state);

        if (!pendingIssueForm.valid) {
          return;
        }

        const { issue, onSuccess, onError } = action.payload;
        const issuePushpin = getActivePushpinAttributes(state);
        const pendingIssue = getPendingIssue(state);
        const { title, description, suggestedAnswer, locationDescription, dueDate } = pendingIssue;
        const issueAttributes = {
          title,
          description,
          suggestedAnswer,
          locationDescription,
          dueDate,
        };

        if (issuePushpin) {
          const pushpinType = issuePushpin.position.z ? 'TwoDVectorPushpin' : 'TwoDRasterPushpin';
          const pushpinAttributes = {
            type: pushpinType,
            location: issuePushpin.position,
            objectId: issuePushpin.objectId ? issuePushpin.objectId.toString() : undefined,
            viewerState: issuePushpin.viewerState ? issuePushpin.viewerState : undefined,
            externalId: issuePushpin.externalId ? issuePushpin.externalId : undefined,
            attributesVersion: issuePushpin.attributesVersion
              ? issuePushpin.attributesVersion
              : undefined,
          };
          issueAttributes.pushpinAttributes = pushpinAttributes;
        }

        store.dispatch(updateIssueAction(issue, issueAttributes, { onSuccess, onError }));

        break;
      }
      case HANDLE_OPEN_ATTACHMENT: {
        docsSDK.getDocumentsByURNs(getProjectId(state), [action.payload.urn]).then((result) => {
          if (result.length > 0) {
            const completeAttachmentData = {
              ...action.payload,
              folderUrn: lodashGet(result[0], 'parentFolderUrn'),
            };
            store.dispatch({
              type: OPEN_ATTACHMENT,
              payload: completeAttachmentData,
            });
          } else {
            const oxygen = getOxygenMappings(state);
            const createdBy = getUserNameDisplayNameWithCompany(
              oxygen,
              action.payload.createdBy,
              null
            );
            const attachmentReadPermissionsErrorWithUserName = T.translate(
              'errors.attachmentReadPermissionsErrorWithUserName',
              { userName: createdBy }
            );
            const errorText = createdBy ? (
              <Text hasHTML>{attachmentReadPermissionsErrorWithUserName}</Text>
            ) : (
              T.translate('errors.attachmentReadPermissionsError')
            );
            store.dispatch(
              showNotificationAction({
                type: NotificationTypes.SNACKBAR,
                level: NotificationLevels.INFO,
                message: errorText,
              })
            );
          }
        });
        break;
      }
      case HANDLE_NAVIGATE_TO_RFI_IN_DOCUMENT: {
        if (!redispatcher.listenerCount(HANDLE_NAVIGATE_TO_RFI_IN_DOCUMENT)) {
          const env = getEnv(state);
          const region = getRegion(state);
          const projectId = getProjectId(state);
          const requestDomainUrl = window.location.origin;
          const baseUrl = `${ENV_CONFIGS[region][env].DOCS}/projects/${projectId}`;
          const issueUrl = `/rfis/${action.payload.issueId}`;
          const urlQueryParams = `?panels=default&layouts=disableTitleBar&requestDomain=${requestDomainUrl}`;

          docsSDK
            .getDocumentsByURNs(getProjectId(state), [action.payload.targetUrn])
            .then((result) => {
              const title = lodashGet(result[0], 'currentVersion.name');
              const url = `${baseUrl}/folders/${lodashGet(
                result[0],
                'parentFolderUrn'
              )}/detail/viewer/items/${action.payload.targetUrn}${issueUrl}${urlQueryParams}`;
              store.dispatch(openIframeModal(url, title));
            });
        }
        break;
      }
      case OPEN_PUSHPIN_SELECTION: {
        const env = getEnv(state);
        const region = getRegion(state);
        const projectId = getProjectId(state);
        const { document } = action.payload;
        const requestDomainUrl = window.location.origin;

        const baseUrl = `${ENV_CONFIGS[region][env].DOCS}/projects/${projectId}`;

        const urlQueryParams = `?droppin=rfis&pinstatus=${action.payload.status}&requestDomain=${requestDomainUrl}`;
        const url = `${baseUrl}/viewer/items/${document.urn}${urlQueryParams}`;
        const locationQuery = {
          [POPUP_VIEWER_URL]: url,
          [POPUP_VIEWER_TITLE]: lodashGet(document, 'currentVersion.name'),
        };

        store.dispatch(
          changeLocationAction({ pathname: action.payload.location.pathname, query: locationQuery })
        );
        break;
      }
      case CLOSE_PUSHPIN_SELECTION: {
        store.dispatch(changeLocationAction({ pathname: action.payload.location.pathname }));
        break;
      }
      case TOGGLE_PUSHPIN_VISIBILITY: {
        const issue = action.payload;
        const documentCollection = getDocumentCollection(state);
        const documentVersion = getDocumentVersion(documentCollection, issue.targetUrn);
        const isHiding = isPushpinVisible(issue);
        const stringPathAccordingToVisibility = getPushpinVisibilityTextPrefix(isHiding);
        const issueIdentifier = issue.customIdentifier || T.translate('unspecifiedValue');

        const onSuccess = (updatedIssue) => {
          const currentUserId = lodashGet(getCurrentUser(state), 'attributes.id');
          const currentAssigneeId = issue.assignedTo || lodashGet(issue, 'assignees[0].id');
          const currentAssignedToType =
            issue.assignedToType || lodashGet(issue, 'assignees[0].type');
          const assignee = getAssigneeTypeByAssigneeId(
            currentAssigneeId,
            currentUserId,
            getOxygenMappings(state),
            currentAssignedToType
          );

          store.dispatch(trackEvent(togglePushpinVisibility(isHiding, assignee)));
          store.dispatch({ type: TOGGLE_PUSHPIN_VISIBILITY_SUCCESS, payload: updatedIssue });
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.SUCCESS,
              message: T.translate(
                `preview.pushpinVisibilityPopup.${stringPathAccordingToVisibility}.successNotification`,
                { identifier: issueIdentifier }
              ),
            })
          );
        };
        const onError = () => {
          store.dispatch({ type: TOGGLE_PUSHPIN_VISIBILITY_FAIL });
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: T.translate(
                `preview.pushpinVisibilityPopup.${stringPathAccordingToVisibility}.errorNotification`,
                { identifier: issueIdentifier }
              ),
            })
          );
        };

        const hiddenDocVersionAttrNAme = ISSUE_ATTRIBUTE_NAMES.HIDDEN_DOC_VERSION;
        store.dispatch(
          updateIssueAction(
            issue,
            {
              [ISSUE_ATTRIBUTE_NAMES.PUSHPIN_ATTRIBUTES]: {
                [hiddenDocVersionAttrNAme]: isHiding ? documentVersion : null,
              },
            },
            { onError, onSuccess }
          )
        );
        break;
      }
      case DELETE_PUSHPIN: {
        const issue = action.payload;
        const entityName = T.translate('rfis.label_capitalized');
        const onSuccess = () => {
          const userRoleForAnalytics = currentUserRoleForAnalytics(state);
          store.dispatch(
            trackEvent(
              fieldEdit(ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENT, issue.status, userRoleForAnalytics)
            )
          );
          store.dispatch(deletePushpinSuccess(issue.id));
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.TOAST,
              level: NotificationLevels.SUCCESS,
              message: T.translate('issueAttributes.document.deletePushpinSuccessNotification', {
                entityName,
              }),
            })
          );
        };
        const onError = () => {
          store.dispatch(deletePushpinFail());
          store.dispatch(
            showNotificationAction({
              type: NotificationTypes.SNACKBAR,
              level: NotificationLevels.ERROR,
              message: T.translate('issueAttributes.document.deletePushpinFailedNotification', {
                entityName,
              }),
            })
          );
        };

        store.dispatch(
          updateIssueAction(
            issue,
            {
              targetUrn: null,
              startingVersion: null,
              pushpinAttributes: null,
              sheetMetadata: null,
            },
            { onError, onSuccess }
          )
        );
        break;
      }
      case VALIDATE_RFI_CUSTOM_IDENTIFIER_START: {
        const { rfiCustomIdentifier } = action.payload;
        store.dispatch({
          type: VALIDATE_RFI_CUSTOM_IDENTIFIER_API,
          meta: action.meta,
          payload: jsonApi.validateRfiCustomIdentifier(rfiCustomIdentifier),
        });
        break;
      }
      case VALIDATE_RFI_CUSTOM_IDENTIFIER_API_FULFILLED: {
        const { onSuccess } = action.meta;
        const isValid = action.payload.isValid && !action.payload.isTaken;
        // Success callback
        onSuccess && onSuccess(isValid);
        break;
      }
      case FETCH_RFI_CUSTOM_IDENTIFIER_START:
        {
          const { isRevision, rfiId } = action.meta;
          store.dispatch({
            type: FETCH_RFI_CUSTOM_IDENTIFIER_API,
            meta: action.meta,
            payload: isRevision
              ? jsonApi.getRfiCustomIdentifierWithRevision(rfiId)
              : jsonApi.getRfiCustomIdentifier(),
          });
        }
        break;
      case FETCH_RFI_CUSTOM_IDENTIFIER_API_FULFILLED: {
        const { onSuccess } = action.meta;
        // Success callback
        onSuccess && onSuccess(action.payload.suggestedNextCustomIdentifier);
        break;
      }

      case ADD_LINKED_ENTITIES: {
        const { issue, onSuccess, onFailure, entities } = action.payload;
        const type = entities.length ? entities[0].linkedItemType : '';
        store.dispatch({
          type: ADD_LINKED_ENTITIES_API,
          meta: {
            id: issue.id,
            type,
            attributes: {},
            onSuccess,
            onFailure,
          },
          payload: jsonApi.createLinks(issue.id, entities),
        });
        break;
      }
      case REMOVE_LINKED_ENTITIES: {
        const { issue, onSuccess, onFailure, item } = action.payload;
        store.dispatch({
          type: REMOVE_LINKED_ENTITIES_API,
          meta: {
            id: issue.id,
            attributes: {},
            onSuccess,
            onFailure,
          },
          payload: jsonApi.deleteLink(issue.id, item),
        });
        break;
      }

      case ADD_LINKED_ENTITIES_API_FULFILLED: {
        store.dispatch(trackEvent(linkReference(action.meta.type)));
        action.meta.onSuccess && action.meta.onSuccess(action.payload.links);
        break;
      }
      case ADD_LINKED_ENTITIES_API_REJECTED: {
        action.meta.onFailure && action.meta.onFailure(action.payload.links);
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: T.translate('errors.referenceCreationError'),
          })
        );
        store.dispatch(trackEvent(linkReference(action.meta.type, action.payload.toString())));
        break;
      }

      case REMOVE_LINKED_ENTITIES_API_FULFILLED: {
        store.dispatch(trackEvent(unlinkReference(action.payload.items[0].linkedItemType)));
        action.meta.onSuccess && action.meta.onSuccess(action.payload.items);
        break;
      }

      case REMOVE_LINKED_ENTITIES_API_REJECTED: {
        action.meta.onFailure && action.meta.onFailure(action.payload.items);
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: T.translate('errors.referenceUnlinkError'),
          })
        );
        break;
      }
      case VIRTUAL_FOLDER_CREATE_START: {
        store.dispatch({
          type: VIRTUAL_FOLDER_CREATE_API,
          meta: action.meta || {},
          payload: jsonApi.createVirtualFolder(),
        });
        break;
      }
      case VIRTUAL_FOLDER_CREATE_API_FULFILLED: {
        if (action.meta.resolve) {
          action.meta.resolve(action.payload);
        }
        break;
      }
      case VIRTUAL_FOLDER_CREATE_API_REJECTED: {
        if (action.meta.reject) {
          action.meta.reject();
        }
        break;
      }
    }

    // eslint-disable-next-line consistent-return
    return next(action);
  };
};
